// frontend/src/components/integrations/IntegrationCard.tsx
import React from "react";
import { cn } from "src/common/utils";
import { Card } from "src/components/ui/card";
import IntegrationIcon from "src/components/ui/IntegrationIcon";

import { INTEGRATION_INFO,IntegrationType } from "./integration-categories";

interface IntegrationCardProps {
  integrationType: IntegrationType;
  isConnected: boolean;
  isPending?: boolean;
  onClick: () => void;
  hideManage?: boolean;
}

export default function IntegrationCard({
  integrationType,
  isConnected,
  isPending = false,
  onClick,
  hideManage = false,
}: IntegrationCardProps) {
  const info = INTEGRATION_INFO[integrationType];
  const isAvailable = info.available !== false;

  // Allow clicking if available OR if already connected (e.g., CloudWatch connected by admin)
  // But don't allow clicking if hideManage is true and already connected
  const isClickable = (isAvailable || isConnected || isPending) && !(hideManage && isConnected);

  const handleClick = () => {
    if (isClickable) {
      onClick();
    }
  };

  const renderStatus = () => {
    if (isConnected) {
      return (
        <div className="flex items-center text-xs mt-1 text-green-500">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5"></div>
          Connected
        </div>
      );
    }
    if (isPending) {
      return (
        <div className="flex items-center text-xs mt-1 text-yellow-500">
          <div className="w-1.5 h-1.5 rounded-full bg-yellow-500 mr-1.5"></div>
          Pending Admin Approval
        </div>
      );
    }
    if (!isAvailable) {
      return (
        <div className="flex items-center text-xs mt-1 text-muted-foreground">
          <div className="w-1.5 h-1.5 rounded-full bg-neutral-500 mr-1.5"></div>
          Not available for your organization
        </div>
      );
    }
    return (
      <div className="flex items-center text-xs mt-1 text-muted-foreground">
        <div className="w-1.5 h-1.5 rounded-full bg-neutral-400 mr-1.5"></div>
        Not Connected
      </div>
    );
  };

  const getActionText = () => {
    if (isConnected) return "Manage";
    if (isPending) return "View Status";
    if (!isAvailable) return "Not available";
    return "Connect";
  };

  return (
    <Card
      interactive={isClickable}
      onClick={handleClick}
      className={cn(
        "p-5 bg-transparent",
        !isClickable && hideManage && isConnected && "cursor-default",
        !isClickable && !(hideManage && isConnected) && "opacity-60 cursor-not-allowed",
      )}
    >
      <div className="flex items-center space-x-4 mb-4">
        <div className="flex-shrink-0">
          <IntegrationIcon type={integrationType} className="h-8 w-8" />
        </div>
        <div className="flex-grow">
          <h3 className="font-medium text-foreground">{info.name}</h3>
          {renderStatus()}
        </div>
      </div>

      {/* Hide action text when hideManage is true and connected */}
      {!(hideManage && isConnected) && (
        <span
          className={`text-sm font-medium pl-12 ${
            isClickable
              ? "text-muted-foreground hover:text-foreground transition-colors"
              : "text-muted-foreground"
          }`}
        >
          {getActionText()}
        </span>
      )}
    </Card>
  );
}
